import React from 'react';
import RiskCategoryBadge, { CATEGORY_CONFIG } from './RiskCategoryBadge';

/**
 * Circular gauge component for risk score visualization
 */
const RiskGauge = ({ score, category, size = 120 }) => {
  const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.low;
  // Map 1-5 score to 0-100% for the gauge ring
  const percentage = Math.max(0, Math.min(100, ((score - 1) / 4) * 100));
  const circumference = 2 * Math.PI * 45; // radius = 45
  const strokeDashoffset = circumference * (1 - percentage / 100);

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg viewBox="0 0 100 100" className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx="50"
          cy="50"
          r="45"
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="8"
        />
        {/* Progress circle */}
        <circle
          cx="50"
          cy="50"
          r="45"
          fill="none"
          stroke={config.color}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          style={{ transition: 'stroke-dashoffset 0.5s ease-in-out' }}
        />
      </svg>
      {/* Center text */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold" style={{ color: config.color }}>
          {score.toFixed(2)}
        </span>
        <span className="text-xs text-gray-500">/ 5.00</span>
      </div>
    </div>
  );
};

/**
 * Trend indicator component
 */
const TrendIndicator = ({ direction, change }) => {
  const trendConfig = {
    increasing: {
      icon: '\u2191',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      label: 'Increasing',
    },
    decreasing: {
      icon: '\u2193',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      label: 'Decreasing',
    },
    stable: {
      icon: '\u2192',
      color: 'text-gray-600',
      bgColor: 'bg-gray-50',
      label: 'Stable',
    },
  };

  const config = trendConfig[direction] || trendConfig.stable;
  const absChange = Math.abs(change || 0);

  return (
    <div className={`inline-flex items-center gap-1 px-2 py-1 rounded ${config.bgColor}`}>
      <span className={`text-lg ${config.color}`}>{config.icon}</span>
      <span className={`text-xs ${config.color}`}>
        {config.label}
        {absChange > 0 && ` (${absChange.toFixed(2)})`}
      </span>
    </div>
  );
};

/**
 * Confidence meter component
 */
const ConfidenceMeter = ({ confidence }) => {
  const percentage = Math.round(confidence * 100);
  const bars = 5;
  const filledBars = Math.round(confidence * bars);

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-500">Model Confidence:</span>
      <div className="flex gap-0.5">
        {[...Array(bars)].map((_, i) => (
          <div
            key={i}
            className={`w-2 h-3 rounded-sm ${
              i < filledBars ? 'bg-blue-500' : 'bg-gray-200'
            }`}
          />
        ))}
      </div>
      <span className="text-xs font-medium text-gray-700">{percentage}%</span>
    </div>
  );
};

/**
 * MlRiskScoreCard - Main card component for displaying risk score
 */
const MlRiskScoreCard = ({ riskScore, loading = false, onRefresh }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-32 mb-4" />
          <div className="flex items-center gap-4">
            <div className="w-24 h-24 bg-gray-200 rounded-full" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-24" />
              <div className="h-3 bg-gray-200 rounded w-32" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!riskScore) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700">Risk Score</h3>
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              Calculate
            </button>
          )}
        </div>
        <div className="text-center py-6 text-gray-500 text-sm">
          <p>No risk score available.</p>
          <p className="text-xs mt-1">Click Calculate to generate a score.</p>
        </div>
      </div>
    );
  }

  const {
    score,
    category,
    trendDirection,
    trendChange,
    modelConfidence,
    calculatedAt,
  } = riskScore;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-700">Risk Score</h3>
        <div className="flex items-center gap-2">
          <RiskCategoryBadge category={category} size="sm" />
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="p-1 text-gray-400 hover:text-gray-600 rounded"
              title="Recalculate"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="flex items-center gap-4">
        {/* Gauge */}
        <RiskGauge score={score} category={category} size={100} />

        {/* Details */}
        <div className="flex-1">
          {/* Trend */}
          <div className="mb-3">
            <TrendIndicator direction={trendDirection} change={trendChange} />
          </div>

          {/* Confidence */}
          <ConfidenceMeter confidence={modelConfidence} />

          {/* Timestamp */}
          {calculatedAt && (
            <div className="mt-2 text-xs text-gray-400">
              Updated: {new Date(calculatedAt).toLocaleString()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MlRiskScoreCard;
export { RiskGauge, TrendIndicator, ConfidenceMeter };
